/**
 * BingX WebSocket Configuration
 *
 * WebSocket channels, topology info, and low-level subscription utilities for BingX.
 * @see https://bingx-api.github.io/docs/#/spot/websocket/market-api.html
 */

import pako from 'pako';
import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  BingxWsDepthMessage,
  BingxWsKlineMessage,
  BingxWsStatus,
  BingxWsTickerMessage,
  BingxWsTradeMessage,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const BINGX_WS_BASE_URL = 'wss://open-api-ws.bingx.com/market';

/**
 * BingX WebSocket channel descriptors.
 * @see https://bingx-api.github.io/docs/#/spot/websocket/market-api.html
 */
export const BINGX_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  depth: {
    channel: 'depth',
    topic: '{symbol}@incrDepth',
    supportsBatching: false,
    docUrl:
      'https://bingx-api.github.io/docs/#/spot/websocket/market-api.html#Partial%20Book%20Depth%20Streams',
  },
  ticker: {
    channel: 'ticker',
    topic: '{symbol}@ticker',
    supportsBatching: false,
    docUrl:
      'https://bingx-api.github.io/docs/#/spot/websocket/market-api.html#Individual%20Symbol%20Ticker%20Streams',
  },
  trades: {
    channel: 'trades',
    topic: '{symbol}@trade',
    supportsBatching: false,
    docUrl: 'https://bingx-api.github.io/docs/#/spot/websocket/market-api.html#Trade%20Streams',
  },
  klines: {
    channel: 'klines',
    topic: '{symbol}@kline_{interval}',
    supportsBatching: false,
    docUrl: 'https://bingx-api.github.io/docs/#/spot/websocket/market-api.html#K-Line%20Streams',
  },
};

/**
 * BingX WebSocket topology and connection rules.
 */
export const BINGX_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: BINGX_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: 25000,
  note: 'All WebSocket messages are gzip-compressed. Server sends ping { ping: ts } which must be responded with { pong: ts }.',
};

/**
 * Decode BingX WebSocket message (gzip-compressed).
 */

export async function decodeBingxWsMessage(
  event: MessageEvent<ArrayBuffer | Blob | string>,
): Promise<any> {
  if (typeof event.data === 'string') {
    return JSON.parse(event.data);
  }

  try {
    const readBlob = async (blob: Blob): Promise<ArrayBuffer> => {
      if (typeof blob.arrayBuffer === 'function') {
        return blob.arrayBuffer();
      }
      if (typeof FileReader !== 'undefined') {
        return await new Promise<ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as ArrayBuffer);
          reader.onerror = () =>
            reject(reader.error ?? new Error('Failed to read BingX WebSocket Blob'));
          reader.readAsArrayBuffer(blob);
        });
      }
      if (typeof Response !== 'undefined') {
        return new Response(blob).arrayBuffer();
      }
      throw new Error('Failed to read BingX WebSocket Blob');
    };

    const buffer = event.data instanceof Blob ? await readBlob(event.data) : event.data;
    const bytes = new Uint8Array(buffer);
    const text = pako.ungzip(bytes, { to: 'string' });
    return JSON.parse(text);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`BingX WebSocket decode error: ${message}`);
  }
}

export interface BingxWsSubscriptionOptions<T> {
  topic: string;
  onMessage: (data: T) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BingxWsStatus) => void;
  baseUrl?: string;
}

/**
 * Create a generic BingX WebSocket subscription.
 * Handles gzip decompression, ping/pong, and automatic reconnection.
 *
 * @returns ReconnectingWebSocket instance
 */
export function createBingxWsSubscription<T>(
  options: BingxWsSubscriptionOptions<T>,
): ReconnectingWebSocket {
  const { topic, onMessage, onError, onStatusChange, baseUrl = BINGX_WS_BASE_URL } = options;

  let hasConnectedOnce = false;
  onStatusChange?.('connecting');

  const ws = new ReconnectingWebSocket(baseUrl, [], {
    maxRetries: 10,
    minReconnectionDelay: 1000,
  });
  ws.binaryType = 'arraybuffer';

  ws.addEventListener('open', () => {
    hasConnectedOnce = true;
    onStatusChange?.('connected');

    const subscribeMsg = {
      id: `sub_${Date.now()}`,
      reqType: 'sub',
      dataType: topic,
    };
    ws.send(JSON.stringify(subscribeMsg));
  });

  ws.addEventListener('message', (event: MessageEvent) => {
    void (async () => {
      try {
        const payload = await decodeBingxWsMessage(event);

        // Handle ping (critical)
        if (payload.ping) {
          ws.send(JSON.stringify({ pong: payload.ping, time: payload.time }));
          return;
        }

        // Forward data
        if (payload.data || payload.dataType === topic) {
          onMessage(payload as T);
        }
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error(String(error)));
      }
    })();
  });

  ws.addEventListener('close', () => {
    onStatusChange?.(hasConnectedOnce ? 'reconnecting' : 'disconnected');
  });

  ws.addEventListener('error', event => {
    onError?.(new Error(`BingX WebSocket error: ${event.type}`));
    onStatusChange?.('error');
  });

  return ws;
}

export function subscribeBingxKlines(params: {
  symbol: string;
  interval: string;
  onMessage: (data: BingxWsKlineMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BingxWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, interval, onMessage, onError, onStatusChange } = params;
  return createBingxWsSubscription<BingxWsKlineMessage>({
    topic: `${symbol}@kline_${interval}`,
    onMessage,
    onError,
    onStatusChange,
  });
}

export function subscribeBingxTicker(params: {
  symbol: string;
  onMessage: (data: BingxWsTickerMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BingxWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createBingxWsSubscription<BingxWsTickerMessage>({
    topic: `${symbol}@ticker`,
    onMessage,
    onError,
    onStatusChange,
  });
}

export function subscribeBingxTrades(params: {
  symbol: string;
  onMessage: (data: BingxWsTradeMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BingxWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createBingxWsSubscription<BingxWsTradeMessage>({
    topic: `${symbol}@trade`,
    onMessage,
    onError,
    onStatusChange,
  });
}

export function subscribeBingxDepth(params: {
  symbol: string;
  onMessage: (data: BingxWsDepthMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BingxWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createBingxWsSubscription<BingxWsDepthMessage>({
    topic: `${symbol}@incrDepth`,
    onMessage,
    onError,
    onStatusChange,
  });
}
